import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchMenus, addMenu, deleteMenu, uploadMenuImage, reorderMenus, updateMenuSuspension } from './menuApi';

export interface MenuItem {
  id: string;
  name: string;
  imageUrl: string | null;
  price: number;
  sortIndex: number;
  isActive: boolean;
  menuOptionGroupId: string | null;
  // 受注停止（売り切れ）。BE ADR 0085 / FE ADR 0054。BE未デプロイ環境でも動くよう optional。
  suspended?: boolean;
  suspendedUntil?: string | null; // "YYYY-MM-DDTHH:mm:ss"（JST壁時計）。null = 恒久停止
  isSuspended?: boolean; // BEが現在時刻で計算した停止中フラグ
}

interface MenuState {
  menus: MenuItem[];
  loading: boolean;
  error: string | null;
}

const initialState: MenuState = {
  menus: [],
  loading: false,
  error: null,
};

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    /**
     * ローカルでメニューの並び替えを更新
     */
    reorderMenusLocally: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      const { fromIndex, toIndex } = action.payload;
      const movedMenu = state.menus.splice(fromIndex, 1)[0];
      state.menus.splice(toIndex, 0, movedMenu);
      // sortIndex を更新
      state.menus.forEach((menu, index) => {
        menu.sortIndex = index + 1;
      });
    }, 
    /**
     * 並び替え後のメニューリストを一括設定
     */
    setMenusOrder: (state, action: PayloadAction<MenuItem[]>) => {
      state.menus = action.payload;
    },
  },
  extraReducers: (builder) => {
    // fetchMenus
    builder
      .addCase(fetchMenus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenus.fulfilled, (state, action: PayloadAction<MenuItem[]>) => {
        state.menus = action.payload;
        state.loading = false;
      })
      .addCase(fetchMenus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // addMenu
    builder.addCase(addMenu.fulfilled, (state, action: PayloadAction<MenuItem>) => {
      if (action.payload && action.payload.id) {
        const newMenu = {
          ...action.payload,
          sortIndex: state.menus.length + 1 // sortIndex を最後尾に設定
        };
        state.menus.push(newMenu);
      }
    });

    // deleteMenu
    builder.addCase(deleteMenu.fulfilled, (state, action: PayloadAction<string>) => {
      const menuId = action.payload;
      state.menus = state.menus.filter((m) => m.id !== menuId);
    });

    // uploadMenuImage
    builder.addCase(uploadMenuImage.fulfilled, (state, action: PayloadAction<{ menuId: string, imageUrl: string }>) => {
      const { menuId, imageUrl } = action.payload;
      const menu = state.menus.find((m) => m.id === menuId);
      if (menu) {
        menu.imageUrl = imageUrl;
      }
    });

    // updateMenuSuspension: サーバーが計算した最新の停止状態で該当メニューを差し替える
    builder.addCase(updateMenuSuspension.fulfilled, (state, action: PayloadAction<MenuItem>) => {
      const index = state.menus.findIndex((m) => m.id === action.payload.id);
      if (index !== -1) {
        state.menus[index] = { ...state.menus[index], ...action.payload };
      }
    });

    // reorderMenus
    builder.addCase(reorderMenus.fulfilled, (state, action) => {
      // 並び替え完了後、最新の状態を反映
      // メタデータだけが更新されているので、このまま保持する
      // 正しい並び順を維持するため、現在のstate.menusをそのまま使用
    });

    builder.addCase(reorderMenus.rejected, (state, action) => {
      state.error = action.payload as string;
    });
  },
});

export const { reorderMenusLocally, setMenusOrder } = menuSlice.actions;

export default menuSlice.reducer;